import { ImagePaths } from '../../lib/imageUrls.js';

export const essentialEightAssessmentCourse = {

    id: 3,
    slug: 'essential-eight-assessment-course',
    ogImage: '/og/courses/essential-eight-assessment-course.jpg',
    courseCode: 'E8-ASSESS-001',
    title: 'Essential Eight Assessment',
    subtitle: 'Learn how to conduct Essential Eight assessments using ACSC guidelines',
    valueProposition: 'Master the skills to assess your organisation\'s Essential Eight maturity and develop actionable remediation plans aligned to ACSC guidelines.',
    duration: '32 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_3,
    shortDescription: 'E8-ASSESS-001 — Essential Eight maturity assessment covering ACSC frameworks, evidence collection, and remediation planning for Australian auditors and IT teams.',
    price: {
      aud: 999,
      currency: 'AUD',
      earlyBird: 799
    },
    stripeConfig: {
    priceId: 'price_1SdKSHKu1nyc322UrD01RrOp',
    productId: 'prod_TaVTQCW0yWFnDQ'
  },
    
    snapshot: {
      whoItsFor: [
        'IT administrators conducting security assessments',
        'Security officers responsible for compliance verification',
        'Compliance managers auditing Essential Eight controls',
        'Consultants assisting organisations with E8 maturity assessment'
      ],
      accreditation: 'Australian Cyber Security Centre (ACSC) Essential Eight Assessment'
    },
    
    logistics: {
      classSize: 'Maximum 12 participants for hands-on practice',
      requirements: [
        'Experience with Essential Eight fundamentals',
        'Access to organisational IT systems and documentation',
        'Administrative access to servers and network infrastructure (or test environment)',
        'Laptop with remote access capabilities'
      ]
    },
    
    overview: 'Become proficient in conducting Essential Eight assessments using official ACSC methodologies. This intensive 32-hour course teaches you how to systematically evaluate your organisation\'s Essential Eight maturity across all eight mitigation strategies. You\'ll learn assessment frameworks, evidence collection techniques, reporting standards, and how to develop detailed remediation roadmaps aligned to industry best practices and Australian compliance requirements.',
    
    outcomes: [
      'Conduct comprehensive Essential Eight maturity assessments using ACSC assessment frameworks',
      'Evaluate all eight mitigation strategies across foundational, intermediate, and advanced maturity levels',
      'Collect and document evidence of Essential Eight control implementation',
      'Identify gaps and prioritise remediation activities based on risk and business impact',
      'Develop practical remediation roadmaps with realistic timelines and resource requirements',
      'Report assessment findings to executive and technical stakeholders',
      'Assist organisations in achieving and maintaining Essential Eight compliance',
      'Guide remediation implementation and verify control effectiveness'
    ],
    
    audience: [
      'IT administrators conducting security assessments',
      'Security officers and compliance managers',
      'Internal and external security auditors',
      'Consultants supporting Essential Eight implementation',
      'Enterprise security teams'
    ],
    
    prerequisites: [
      'Understanding of Essential Eight fundamentals',
      'Experience with IT systems administration',
      'Knowledge of network and security infrastructure',
      'Familiarity with compliance assessment concepts'
    ],
    
    modules: [
      {
        title: 'Essential Eight Assessment Frameworks',
        duration: '6 hours',
        topics: [
          'ACSC assessment methodology and standards',
          'Maturity model overview and definitions',
          'Assessment planning and scope definition',
          'Stakeholder engagement and communication',
          'Assessment tools and documentation requirements'
        ]
      },
      {
        title: 'Application Control and Patch Management Assessment',
        duration: '6 hours',
        topics: [
          'Application control assessment techniques',
          'Whitelist and blacklist evaluation',
          'Patch management process assessment',
          'Operating system patching verification',
          'Application update and deployment review'
        ]
      },
      {
        title: 'User Access and Authentication Assessment',
        duration: '5 hours',
        topics: [
          'Administrative privilege assessment',
          'Multi-factor authentication evaluation',
          'User access rights review and verification',
          'Privileged access management assessment',
          'Password policy and enforcement review'
        ]
      },
      {
        title: 'Backup and Recovery Assessment',
        duration: '5 hours',
        topics: [
          'Backup strategy and scope review',
          'Backup frequency and retention assessment',
          'Recovery testing and validation',
          'Ransomware resilience evaluation',
          'Backup media and secure storage assessment'
        ]
      },
      {
        title: 'Evidence Collection and Documentation',
        duration: '4 hours',
        topics: [
          'Evidence gathering techniques and tools',
          'Documentation standards and templates',
          'Record management and audit trail creation',
          'Screenshots, logs, and configuration evidence',
          'Building assessment evidence packages'
        ]
      },
      {
        title: 'Reporting and Remediation Planning',
        duration: '6 hours',
        topics: [
          'Assessment report structure and content',
          'Communicating findings to stakeholders',
          'Risk prioritisation and remediation planning',
          'Implementation timelines and resource planning',
          'Monitoring and verification strategies'
        ]
      }
    ],
    
    format: {
      type: 'Instructor-led online with practical assessments',
      includes: [
        '32 hours of comprehensive instruction',
        'Hands-on assessment practice labs',
        'ACSC assessment templates and checklists',
        'Remediation roadmap tools and examples',
        'Assessment report templates',
        'Case studies and real-world scenarios',
        'Certificate of completion',
        '9 months access to course materials'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Four-day intensive program (8 hours per day) with optional follow-up mentoring'
    },
    
    faqs: [
      {
        question: 'Is this course suitable if I\'m new to Essential Eight?',
        answer: 'While this course focuses on assessment techniques, you should have foundational knowledge of Essential Eight. We recommend taking our Essential Eight Fundamentals course first if you\'re completely new to the framework.'
      },
      {
        question: 'Will I learn to use assessment tools?',
        answer: 'Yes, we cover industry-standard assessment tools and techniques, including automated scanning and manual verification methods. You\'ll learn how to combine multiple evidence sources for comprehensive assessments.'
      },
      {
        question: 'Can I perform assessments on my own organisation?',
        answer: 'Absolutely! This course is designed for those conducting assessments within their own organisations. You\'ll gain practical experience and can apply the skills immediately in your environment.'
      },
      {
        question: 'Does this prepare me for consultant work?',
        answer: 'Yes, many participants use this course to develop consulting capabilities. You\'ll learn assessment methodologies, evidence collection, and reporting that are valuable for assisting other organisations.'
      },
      {
        question: 'What documentation will I receive?',
        answer: 'You\'ll receive ACSC-aligned assessment templates, evidence collection checklists, reporting frameworks, remediation roadmap tools, and all course materials for ongoing reference.'
      },
      {
        question: 'How long does a typical assessment take?',
        answer: 'Assessment duration varies by organisation size and complexity, typically ranging from 1-3 weeks for a medium-sized organisation. The course teaches you how to plan and estimate assessment timelines.'
      },
      {
        question: 'Will this help me with government contractor compliance?',
        answer: 'Yes, if your organisation is a government contractor, this course directly supports your ability to demonstrate Essential Eight compliance to government bodies.'
      },
      {
        question: 'Is this course available for in-house delivery?',
        answer: 'Yes, we can deliver customised in-house training tailored to your organisation\'s specific Essential Eight assessment needs and maturity level. Contact us for pricing and scheduling options.'
      }
    ]
};
